import { useCallback, useEffect, useRef, useState } from 'react';
import type { LegalDocumentDTO } from '../../global';

export type OnboardingStatus = 'idle' | 'checking' | 'required' | 'done' | 'error';

export interface OnboardingHookState {
  status: OnboardingStatus;
  /** Güncel sürümü onaylanmamış yasal metinler. */
  pending: LegalDocumentDTO[];
  /** Bu cihazda "Hazırsın" karşılaması (ve ardından ürün turu) gösterilmedi. */
  needsSetup: boolean;
  error: string | null;
}

const IDLE: OnboardingHookState = { status: 'idle', pending: [], needsSetup: false, error: null };

/**
 * Girişten sonraki kapı: yasal onaylar (hesaba bağlı, sunucuda) ve ilk kurulum
 * (bilgisayara bağlı). Sunucu bir istekte "yeni metinleri onayla" derse
 * (`consent_required`) durum yeniden okunur ve kapı tekrar açılır.
 */
export function useOnboarding(userId: string | null) {
  const [state, setState] = useState<OnboardingHookState>(IDLE);
  const userRef = useRef(userId);
  userRef.current = userId;

  const refresh = useCallback(async () => {
    const forUser = userRef.current;
    if (!forUser) return;
    setState((prev) => ({ ...prev, status: prev.status === 'required' ? 'required' : 'checking', error: null }));
    try {
      const result = await window.zabit.getOnboardingState();
      // Bu arada çıkış yapıldıysa ya da hesap değiştiyse eski yanıtı yazma.
      if (userRef.current !== forUser) return;
      setState({
        status: result.pending.length > 0 || result.needsSetup ? 'required' : 'done',
        pending: result.pending,
        needsSetup: result.needsSetup,
        error: null,
      });
    } catch (err) {
      if (userRef.current !== forUser) return;
      setState((prev) => ({ ...prev, status: 'error', error: (err as Error).message }));
    }
  }, []);

  useEffect(() => {
    if (userId) void refresh();
    else setState(IDLE);
  }, [userId, refresh]);

  useEffect(() => window.zabit.onConsentRequired(() => void refresh()), [refresh]);

  /** Gösterilen sürümleri onaylar ve durumu yeniden okur (409'da yeni sürüm gelir). */
  const accept = useCallback(
    async (documents: LegalDocumentDTO[]) => {
      try {
        await window.zabit.acceptLegal(documents.map(({ kind, version }) => ({ kind, version })));
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  const complete = useCallback(async () => {
    await window.zabit.completeOnboarding();
    setState((prev) => ({ ...prev, status: prev.pending.length > 0 ? 'required' : 'done', needsSetup: false }));
  }, []);

  return { ...state, refresh, accept, complete };
}
